import { useState, useEffect } from 'react';
import './TresholdProgress.css';
import icon_target from '../../assets/icons/target.svg';
import icon_check_blue from '../../assets/icons/check-blue.svg';
import icon_check_green from '../../assets/icons/answer_correct.svg';

export default function TresholdProgress({
  totalScore = null,
  maximumScore = 0,
  resultScore = 0,
  thresholds = [],
}) {
  const [animatedScore, setAnimatedScore] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);

  const validMaximumScore = Math.max(maximumScore || 0, 1); 
  const validResultScore = Math.max(
    Math.min(resultScore || 0, validMaximumScore),
    0
  ); 
  const validThresholds = Array.isArray(thresholds) ? thresholds : [];
  const progressBarScore = totalScore !== null ? Math.max(totalScore, 0) : validResultScore;

  // Start animation when component mounts
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsAnimating(true);
      animateToScore();
    }, 300); // Delay before starting animation

    return () => clearTimeout(timer);
  }, [progressBarScore]); 

  const animateToScore = () => {
    const duration = 1000; 
    const startTime = Date.now();
    const startScore = 0;
    const endScore = Math.min(progressBarScore, validMaximumScore); 

    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);

      const easeOutCubic = 1 - Math.pow(1 - progress, 3);
      const currentScore = startScore + (endScore - startScore) * easeOutCubic;

      setAnimatedScore(Math.round(currentScore));

      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };

    requestAnimationFrame(animate);
  };

  // Calculate progress percentage
  const progressPercentage = validMaximumScore
    ? (animatedScore / validMaximumScore) * 100
    : 0;

  // Get passing thresholds sorted by score
  const passingThresholds = validThresholds
    .filter((threshold) => threshold.is_passing)
    .sort((a, b) => a.score - b.score);

  // Determine if user passed any passing threshold
  const passedThresholds = passingThresholds.filter(
    (threshold) => animatedScore >= threshold?.score
  );
  const currentlyPassed = passedThresholds?.length > 0;

  const getProgressGradient = () => {
    if (currentlyPassed) {
      return 'linear-gradient(90deg, #4F95CC 0%, #296D9F 100%)';
    } else {
      return 'linear-gradient(90deg, #DF812A 0%, #BC2A2A 100%)';
    }
  };


  // Don't render if no valid data
  if (validMaximumScore <= 0) {
    return null;
  }    return (
    <div className={`threshold-progress-container ${totalScore !== null ? 'threshold-progress-container-with-score' : ''}`}>
      <div className='progress-wrapper'>
        <div className='progress-track'>
          {/* Only show progress fill if score is greater than 0 */}
          {progressBarScore > 0 && (
            <div
              className={`progress-fill ${currentlyPassed ? 'passed' : 'failed'}`}
              style={{
                width: progressPercentage >= 100 ? '100%' : `calc(${progressPercentage}% + 7px)`, 
                background: getProgressGradient(),
                transition: isAnimating
                  ? 'width 2.5s cubic-bezier(0.25, 0.46, 0.45, 0.94)'
                  : 'none',
              }}
            />
          )}

          {/* Only show threshold markers if score is greater than 0 */}
          {validThresholds.map((threshold, index) => {
            const thresholdPercentage = maximumScore
              ? (threshold.score / maximumScore) * 100
              : 0;
            const isThresholdReached = animatedScore >= threshold.score;
            const isPassingThreshold = threshold.is_passing;

            return (
              <div
                key={index}
                className={`threshold-marker ${
                  isThresholdReached ? 'reached' : 'not-reached'
                } ${isPassingThreshold ? 'passing' : 'non-passing'}`}
                style={{ 
                  left: thresholdPercentage >= 100 ? '100%' : `calc(${thresholdPercentage}% - 2.75px)` 
                }}
                title={`${threshold.name}: ${threshold.score}/${maximumScore}${
                  isPassingThreshold ? ' (Soglia di superamento)' : ''
                }`}
              >
                <div className='threshold-dot'>
                  {isPassingThreshold && (
                    <img
                      src={currentlyPassed ? icon_check_blue : icon_target}
                      alt={currentlyPassed ? 'Passed' : 'Target'}
                      className='threshold-icon'
                      width={20}
                      height={20}
                    />
                  )}
                </div>
                {!currentlyPassed && (
                  <div className='threshold-label'>
                    {threshold.score}
                    <img
                      src={icon_check_green}
                      alt='Threshold Icon'
                      width={12}
                      height={12}
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
      
      {/* Display totalScore/maximumScore on the right side if totalScore is available */}
      {totalScore !== null && (
        <div className='score-display'>
          <span className='score-display-total'>
            {Math.round((totalScore + Number.EPSILON) * 100) / 100}
          </span>
          <span className='score-display-max'>
            /{validMaximumScore}
          </span>
        </div>
      )}
    </div>
  );
}
